import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback } from "react";
import { Button, Badge, Tooltip, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, DialogTrigger, Text, Spinner, MessageBar, MessageBarBody, } from "@fluentui/react-components";
import { PersonSwapRegular, DismissCircleRegular } from "@fluentui/react-icons";
import { useAuth } from "../../context/AuthContext";
import { useRole } from "../../context/RoleContext";
import { useDemo } from "../../context/DemoContext";
import { PeoplePicker } from "./PeoplePicker";
import { getAllRoles, getAllApprobateurs } from "../../services/dataService";
const ROLE_LABELS = {
    Admin: "Admin",
    SuperUser: "Super-utilisateur",
    RH: "RH",
    Manager: "Responsable de service",
    Salarie: "Salarié",
};
const ROLE_COLORS = {
    Admin: "danger",
    SuperUser: "important",
    RH: "informative",
    Manager: "brand",
    Salarie: "subtle",
};
export const RoleSwitcher = () => {
    const { apiToken, graphToken, realUserEmail, realUserName } = useAuth();
    const accessToken = apiToken;
    const { role, isDemoMode, canSimulateRole } = useRole();
    const { demoUser, setDemoUser } = useDemo();
    const [open, setOpen] = useState(false);
    const [picked, setPicked] = useState(null);
    const [lookingUpRole, setLookingUpRole] = useState(false);
    const [error, setError] = useState(null);
    const handlePick = useCallback((u) => {
        setPicked(u);
        setError(null);
    }, []);
    const handleClearPick = useCallback(() => {
        setPicked(null);
        setError(null);
    }, []);
    const handleConfirm = useCallback(async () => {
        if (!picked || !picked.mail || !accessToken)
            return;
        setLookingUpRole(true);
        setError(null);
        try {
            const mailLower = picked.mail.toLowerCase();
            // 1. Rôle explicite dans RolesUtilisateurs
            const roles = await getAllRoles(accessToken);
            const explicit = roles.find((r) => (r.userEmail || "").toLowerCase() === mailLower && r.actif);
            let inferredRole = explicit?.role || "Salarie";
            // 2. Si pas de rôle explicite, auto-détection : si le user figure dans la liste Approbateurs
            //    (N+1/N+2 d'un service), il est Manager de fait.
            if (inferredRole === "Salarie") {
                const approbateurs = await getAllApprobateurs(accessToken);
                const isApprobateur = approbateurs.some((a) => (a.employeeEmail || "").toLowerCase() === mailLower
                    || (a.approbateur2Email || "").toLowerCase() === mailLower);
                if (isApprobateur)
                    inferredRole = "Manager";
            }
            setDemoUser({ email: picked.mail, name: picked.displayName, role: inferredRole });
            setOpen(false);
            setPicked(null);
        }
        catch (e) {
            setError(e.message || "Impossible de récupérer le rôle");
        }
        finally {
            setLookingUpRole(false);
        }
    }, [picked, accessToken, setDemoUser]);
    const handleExit = useCallback(() => {
        setDemoUser(null);
    }, [setDemoUser]);
    if (!canSimulateRole)
        return null;
    return (_jsxs("div", { style: { display: "flex", alignItems: "center", gap: 6 }, children: [isDemoMode && demoUser && (_jsx(Tooltip, { content: `Mode démo — vous voyez l'app comme ${demoUser.name} (${demoUser.email}) avec le rôle ${ROLE_LABELS[demoUser.role]}. Compte réel : ${realUserName}`, relationship: "label", children: _jsxs(Badge, { appearance: "filled", color: "warning", icon: _jsx(PersonSwapRegular, {}), children: ["D\u00C9MO : ", demoUser.name, " (", ROLE_LABELS[demoUser.role], ")"] }) })), isDemoMode ? (_jsx(Button, { size: "small", appearance: "subtle", icon: _jsx(DismissCircleRegular, {}), onClick: handleExit, title: "Revenir \u00E0 mon compte", children: "Quitter la d\u00E9mo" })) : (_jsx(Button, { size: "small", appearance: "subtle", icon: _jsx(PersonSwapRegular, {}), onClick: () => setOpen(true), title: "Simuler un utilisateur pour la d\u00E9mo", children: "Simuler" })), _jsx(Dialog, { open: open, onOpenChange: (_, d) => setOpen(d.open), children: _jsx(DialogSurface, { children: _jsxs(DialogBody, { children: [_jsx(DialogTitle, { children: "Simuler un utilisateur" }), _jsx(DialogContent, { children: _jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 12 }, children: [_jsxs(Text, { size: 200, style: { color: "#666" }, children: ["Choisissez un compte de l'annuaire. Vous verrez l'application comme si vous \u00E9tiez cet utilisateur (identit\u00E9 et r\u00F4le). Les appels backend utilisent toujours votre vrai compte (", realUserEmail, ")."] }), _jsx(PeoplePicker, { label: "Utilisateur \u00E0 simuler", required: true, selectedName: picked?.displayName || "", selectedEmail: picked?.mail || "", onSelect: handlePick, onClear: handleClearPick, accessToken: graphToken || "", placeholder: "Rechercher dans l'annuaire..." }), error && (_jsx(MessageBar, { intent: "error", children: _jsx(MessageBarBody, { children: error }) })), !graphToken && (_jsx(MessageBar, { intent: "warning", children: _jsx(MessageBarBody, { children: "Token Graph indisponible \u2014 la recherche ne peut pas fonctionner." }) }))] }) }), _jsxs(DialogActions, { children: [_jsx(DialogTrigger, { disableButtonEnhancement: true, children: _jsx(Button, { appearance: "secondary", children: "Annuler" }) }), _jsx(Button, { appearance: "primary", disabled: !picked || !picked.mail || lookingUpRole, icon: lookingUpRole ? _jsx(Spinner, { size: "tiny" }) : undefined, onClick: handleConfirm, children: "Simuler cet utilisateur" })] })] }) }) })] }));
};
// Export pour compat
export { ROLE_LABELS, ROLE_COLORS };
